'use client';

import React, { useCallback, useState, useMemo, Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import { Configure } from 'react-instantsearch';
// eslint-disable-next-line import/no-unresolved
import { useSearchParams } from 'next/navigation';
import { rememberAlgoliaUserToken } from 'commons/algoliaUserToken';
import { ALGOLIA_INDEX_NAME, NUMBER_OF_SWATCHES_TO_SHOW, EMPTY_SHOWROOM } from 'commons/constants';
import isClient from 'commons/isClient';
import { createSearchClient } from 'components/essentials/AlgoliaSearchClient';
import CatchAlgoliaSearchError from 'components/essentials/CatchAlgoliaSearchError';
import AdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/AdvancedSearchContext';
import { useTransition } from 'components/consumer/consumer-search/advanced-search-next/hooks/reactShims';
import addDerivedProductData from 'hooks/useProductListingData/helpers/addDerivedProductData';
import getLowestTierVariantFirst from 'hooks/useProductListingData/helpers/getLowestTierVariantFirst';
import CategoryStoresContext from 'data-hooks/context/CategoryStoresContext';
import useProductStoresForCategory from 'data-hooks/useProductStoresForCategory';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import ShowroomDisplayContext from './hooks/ShowroomDisplayContext';
import CategoryAlgoliaLayout from './components/CategoryAlgoliaLayout';
import CategoryAlgoliaIndexSync from './components/CategoryAlgoliaIndexSync';
import TriggerInitialSearch from './components/TriggerInitialSearch';
import ShowroomDataLoader from './components/ShowroomDataLoader';
import CategoryPageColorRestoration from './components/CategoryPageColorRestoration';
import categoryPageAlgoliaInstance from './categoryPageAlgoliaInstance';
import useCategoryPageURL from './hooks/useCategoryPageURL';
import buildCategoryAlgoliaConfigureFilters from './buildCategoryAlgoliaConfigureFilters';
import buildCategoryAlgoliaNumericFilters from './buildCategoryAlgoliaNumericFilters';
import useCategoryAlgoliaAlexandriaData from './hooks/useCategoryAlgoliaAlexandriaData';
import { PAGE_TYPE, appendInStockWarehouseAttributeIfZip, getCategoryPageKey, getCategoryPageTypeConfig, getPageTypeForSlug } from './categoryPageTypes';
import { getSortByFromProductIndex, parseCategoryPageUrlState, serializeFilterState } from './categoryPageUrlState';
const applyParsedUrlState = (parsed, setters) => {
  setters.setCurrentProductIndex(parsed.currentProductIndex);
  setters.setIsReadyToShipActive(parsed.isReadyToShipActive);
  setters.setMinWidth(parsed.minWidth);
  setters.setMaxWidth(parsed.maxWidth);
  setters.setSelectedColors(parsed.selectedColors);
  setters.setSearchQuery(parsed.searchQuery);
  setters.setSelectedFacets(parsed.selectedFacets);
};
const CategoryPageAlgolia = ({
  slug,
  serverCategoryData,
  pageType: pageTypeProp,
  algoliaUserToken
}) => {
  const categorySearchClient = useMemo(() => createSearchClient(algoliaUserToken), [algoliaUserToken]);
  useEffect(() => {
    rememberAlgoliaUserToken(algoliaUserToken);
  }, [algoliaUserToken]);

  // Warehouse-scoped types come from the route; New Arrivals has no route of
  // its own and is only identifiable by its slug.
  const pageType = pageTypeProp ?? getPageTypeForSlug(slug);
  const pageConfig = getCategoryPageTypeConfig(pageType);
  const pageKey = getCategoryPageKey(pageType, slug);
  const alexandriaCategoryData = useCategoryAlgoliaAlexandriaData({
    slug,
    serverCategoryData,
    pageType
  });
  const warehouseId = useWarehouseIdForUser();
  const hasUserZip = !!useZipCodeFromUser();
  const filters = useMemo(() => alexandriaCategoryData?.getCategoryPage?.filters ?? [], [alexandriaCategoryData?.getCategoryPage?.filters]);
  const {
    pagesUrlParam,
    parsedUrlState,
    updateUrlParamImmediate,
    syncFilterStateToUrl
  } = useCategoryPageURL(slug, filters, pageType);
  const [currentProductIndex, setCurrentProductIndex] = useState(parsedUrlState.currentProductIndex);
  const [isReadyToShipActive, setIsReadyToShipActive] = useState(parsedUrlState.isReadyToShipActive);
  const [minWidth, setMinWidth] = useState(parsedUrlState.minWidth);
  const [maxWidth, setMaxWidth] = useState(parsedUrlState.maxWidth);
  const [selectedColors, setSelectedColors] = useState(parsedUrlState.selectedColors);
  const [searchQuery, setSearchQuery] = useState(parsedUrlState.searchQuery);
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState(parsedUrlState.selectedFacets);
  const [colorIdsFromUrl, setColorIdsFromUrl] = useState(parsedUrlState.colorIds);
  const [, startTransition] = useTransition();
  const [showroomData, setShowroomData] = useState(EMPTY_SHOWROOM);

  // Multi-store showroom (CON-8441): only fetched with ?showMultipleStores=true
  // (preserved across filter URL rewrites). Consumed by
  // ProductCardOnDisplayInShowroom deep inside the product grid.
  const searchParams = useSearchParams();
  const showMultipleStores = searchParams?.get('showMultipleStores') === 'true';
  const categoryStores = useProductStoresForCategory({
    categorySlug: slug,
    skip: !showMultipleStores
  });
  const filterStateRef = useRef({});
  filterStateRef.current = {
    sortBy: getSortByFromProductIndex(currentProductIndex, pageType),
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets
  };
  const filterSignatureRef = useRef(serializeFilterState(filterStateRef.current));
  const syncToUrl = useCallback((partialState, {
    resetPages = false
  } = {}) => {
    const nextState = {
      ...filterStateRef.current,
      ...partialState
    };
    const nextSignature = serializeFilterState(nextState);
    if (nextSignature === filterSignatureRef.current) return;
    filterSignatureRef.current = nextSignature;
    syncFilterStateToUrl(nextState, {
      resetPages
    });
  }, [syncFilterStateToUrl]);
  const stateSetters = useMemo(() => ({
    setCurrentProductIndex,
    setIsReadyToShipActive,
    setMinWidth,
    setMaxWidth,
    setSelectedColors,
    setSearchQuery,
    setSelectedFacets
  }), []);
  useEffect(() => {
    if (!isClient()) return undefined;
    const handlePopState = () => {
      const parsed = parseCategoryPageUrlState(new URLSearchParams(window.location.search), filters, pageType);
      const nextSignature = serializeFilterState({
        sortBy: parsed.sortBy,
        isReadyToShipActive: parsed.isReadyToShipActive,
        minWidth: parsed.minWidth,
        maxWidth: parsed.maxWidth,
        searchQuery: parsed.searchQuery,
        selectedColors: parsed.selectedColors,
        selectedFacets: parsed.selectedFacets
      });
      if (nextSignature === filterSignatureRef.current) return;
      filterSignatureRef.current = nextSignature;
      applyParsedUrlState(parsed, stateSetters);
      setColorIdsFromUrl(parsed.colorIds);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [filters, stateSetters, pageType]);
  const updateCurrentProductIndex = useCallback(indexName => {
    startTransition(() => {
      setCurrentProductIndex(indexName);
    });
    syncToUrl({
      sortBy: getSortByFromProductIndex(indexName, pageType)
    }, {
      resetPages: true
    });
  }, [startTransition, syncToUrl, pageType]);
  const handleToggleReadyToShip = useCallback(checked => {
    setIsReadyToShipActive(checked);
    syncToUrl({
      isReadyToShipActive: checked
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleWidthChange = useCallback((min, max) => {
    setMinWidth(min);
    setMaxWidth(max);
    syncToUrl({
      minWidth: min,
      maxWidth: max
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const selectColor = useCallback(colorLabel => {
    const nextSelectedColors = {
      ...filterStateRef.current.selectedColors,
      [colorLabel]: !filterStateRef.current.selectedColors?.[colorLabel]
    };
    setSelectedColors(nextSelectedColors);
    syncToUrl({
      selectedColors: nextSelectedColors
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const clearSelectedColors = useCallback(() => {
    setSelectedColors({});
    syncToUrl({
      selectedColors: {}
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleToggleFacet = useCallback((attribute, value) => {
    const prev = filterStateRef.current.selectedFacets;
    const current = prev[attribute] || new Set();
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    const result = {
      ...prev
    };
    if (next.size === 0) {
      delete result[attribute];
    } else {
      result[attribute] = next;
    }
    setSelectedFacets(result);
    syncToUrl({
      selectedFacets: result
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const clearSelectedFacets = useCallback(() => {
    setSelectedFacets({});
    syncToUrl({
      selectedFacets: {}
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleSearchQueryChange = useCallback(value => {
    setSearchQuery(value);
    syncToUrl({
      searchQuery: value
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const context = useMemo(() => ({
    currentSearchSection: pageConfig.searchSection,
    currentProductIndex,
    updateSearchSection: () => {},
    updateCurrentProductIndex,
    isReadyToShipActive,
    handleToggleReadyToShip,
    minWidth,
    maxWidth,
    handleWidthChange,
    selectedColors,
    selectColor,
    clearSelectedColors,
    pagesUrlParam,
    activeTypeFilter,
    setActiveTypeFilter,
    updateUrlParamImmediate,
    selectedFacets,
    clearSelectedFacets,
    searchQuery,
    onSearchQueryChange: handleSearchQueryChange,
    syncFilterStateToUrl
  }), [currentProductIndex, updateCurrentProductIndex, isReadyToShipActive, handleToggleReadyToShip, minWidth, maxWidth, handleWidthChange, selectedColors, selectColor, clearSelectedColors, pagesUrlParam, activeTypeFilter, updateUrlParamImmediate, selectedFacets, clearSelectedFacets, searchQuery, handleSearchQueryChange, syncFilterStateToUrl, pageConfig.searchSection]);
  const configureFilters = useMemo(() => buildCategoryAlgoliaConfigureFilters({
    slug,
    isReadyToShipActive,
    selectedFacets,
    warehouseId,
    hasUserZip,
    pageType
  }), [slug, isReadyToShipActive, selectedFacets, warehouseId, hasUserZip, pageType]);
  const attributesToRetrieve = useMemo(() => appendInStockWarehouseAttributeIfZip(pageConfig.attributesToRetrieve, hasUserZip, isReadyToShipActive), [hasUserZip, isReadyToShipActive, pageConfig.attributesToRetrieve]);
  const initialProducts = useMemo(() => {
    const category = alexandriaCategoryData?.getCategoryPage;
    if (!category?.products?.length) return [];
    const upholsteryOptions = category.upholsteryOptions || [];
    return category.products.map((product, index) => {
      if (!(product?.variants?.length > 0)) return null;
      const derived = addDerivedProductData({
        product,
        upholsteryOptions
      });
      const orderedVariants = getLowestTierVariantFirst(derived.variants, derived.id);
      const variantsToShow = (orderedVariants?.length > 0 ? orderedVariants : derived.variants).slice(0, NUMBER_OF_SWATCHES_TO_SHOW);
      return {
        ...derived,
        variantsToShow,
        moreVariantsCount: variantsToShow.length ? (derived.variantsTotal ?? derived.variants.length) - variantsToShow.length : 0,
        position: index + 1
      };
    });
  }, [alexandriaCategoryData]);
  const numericFilters = useMemo(() => buildCategoryAlgoliaNumericFilters({
    minWidth,
    maxWidth,
    pageType
  }), [minWidth, maxWidth, pageType]);
  const initialUiState = useMemo(() => ({
    [ALGOLIA_INDEX_NAME.PRODUCTS]: {
      query: parsedUrlState.searchQuery || '',
      ...(parsedUrlState.colorIds.length > 0 ? {
        refinementList: {
          option_color: parsedUrlState.colorIds
        }
      } : {})
    }
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return <AdvancedSearchContext.Provider value={context}>
      <CategoryStoresContext.Provider value={categoryStores}>
        <ShowroomDisplayContext.Provider value={showroomData}>
          <InstantSearchNext key={pageKey} searchClient={categorySearchClient} indexName={ALGOLIA_INDEX_NAME.PRODUCTS} instance={categoryPageAlgoliaInstance} initialUiState={initialUiState} future={{
          preserveSharedStateOnUnmount: false
        }}>
            <CatchAlgoliaSearchError />
            <Configure facets={['*']} filters={configureFilters} numericFilters={numericFilters} hitsPerPage={pageConfig.hitsPerPage} attributesToRetrieve={attributesToRetrieve} clickAnalytics />
            <TriggerInitialSearch />
            <CategoryAlgoliaIndexSync indexName={currentProductIndex} />
            <CategoryPageColorRestoration colorIds={colorIdsFromUrl} />
            <Suspense fallback={null}>
              <ShowroomDataLoader onLoad={setShowroomData} />
            </Suspense>

            <CategoryAlgoliaLayout key={pageKey} slug={slug} pageType={pageType} serverCategoryData={alexandriaCategoryData} initialProducts={initialProducts} selectedFacets={selectedFacets} onToggleFacet={handleToggleFacet} onClearFacets={clearSelectedFacets} />
          </InstantSearchNext>
        </ShowroomDisplayContext.Provider>
      </CategoryStoresContext.Provider>
    </AdvancedSearchContext.Provider>;
};
CategoryPageAlgolia.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object,
  pageType: PropTypes.oneOf(Object.values(PAGE_TYPE)),
  algoliaUserToken: PropTypes.string
};
export default CategoryPageAlgolia;